import './UserProfilePage.css';
import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Avatar, LinearProgress, Alert, Link } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import axiosInstance from '../axiosConfig';
import { ProfileContext } from '../context/ProfileContext';
import API_URL from '../config';

const UserProfilePage = () => {
  const [userProfile, setUserProfile] = useState(null); // Speichert das Benutzerprofil
  const [isEditing, setIsEditing] = useState(false); // Bearbeitungsmodus
  const { setProfileImage } = React.useContext(ProfileContext);
  const [editedProfile, setEditedProfile] = useState({
    name: '',
    email: '',
    schoolName: '',
    profileImageUrl: '',
  });

  const [schoolSearchResults, setSchoolSearchResults] = useState([]); // Speichert die Suchvorschläge
  const [selectedSchool, setSelectedSchool] = useState(null); // Speichert die ausgewählte Schule oder null
  const [isCreatingNewSchool, setIsCreatingNewSchool] = useState(false); // Ob eine neue Schule erstellt wird
  const [inputValue, setInputValue] = useState(''); // State für das Eingabefeld
  const [confirmationFile, setConfirmationFile] = useState(null); // Speichert die Datei der Schulbestätigung
  const [confirmationPreview, setConfirmationPreview] = useState(''); // Vorschau für Bild oder PDF
  const [shouldReloadProfile, setShouldReloadProfile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [emailConfirmationPending, setEmailConfirmationPending] = useState(false);

  const handleProfileImageChange = (newImageUrl) => {
    setProfileImage(newImageUrl);
  };

  const handleUserProfileLogoUpload = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    
    if (file) {
      const formData = new FormData();
      formData.append('profileImage', file);
      const token = localStorage.getItem('authToken');

      setIsUploading(true); // Upload beginnt
      axiosInstance.post('/profile/upload-user-profile-picture', formData, {
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      })
      .then((response) => {
        const newProfileImageUrl = response.data.profileImageUrl;
        setEditedProfile((prevProfile) => ({
          ...prevProfile,
          profileImageUrl: newProfileImageUrl,
        }));
        handleProfileImageChange(newProfileImageUrl);
        setIsUploading(false); // Upload beendet
        setIsEditing(true);
      })
      .catch((error) => {
        console.error('Fehler beim Upload des Profilbildes:', error);
        setIsUploading(false); // Upload beendet
        setIsEditing(true);
      });
    }
  };

  // Upload der Schulbestätigung
  const handleSchoolConfirmationUpload = (e) => {
    // Avoid auto submit when clicking on upload button
    e.preventDefault();
    const file = e.target.files[0];
    if (file) {
      setConfirmationFile(file);
      const fileUrl = URL.createObjectURL(file);
      setConfirmationPreview(fileUrl);
      const formData = new FormData();
      formData.append('school-verification', file);
      const token = localStorage.getItem('authToken');
  
      setIsUploading(true); // Upload beginnt
      axiosInstance.post('/profile/upload-school-verification', formData, {
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted); // Progressbar aktualisieren
        }
      })
      .then((response) => {
        const schoolVerificationDocument = response.data.schoolVerificationDocument;;
  
        // Das Profil im Bearbeitungsmodus aktualisieren
        setEditedProfile((prevProfile) => ({
          ...prevProfile,
          schoolVerificationDocument: schoolVerificationDocument,
        }));
        setIsUploading(false); // Upload beendet
        setIsEditing(true);
      })
      .catch((error) => {
        console.error('Fehler beim Upload des Logos:', error);
        setIsUploading(false); // Upload beendet
        setIsEditing(true);
      });
    }
  };

  useEffect(() => {
    if (alert && alert.severity !== "info") {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 3000); // 3 Sekunden Timeout
  
      return () => clearTimeout(timer); // Cleanup-Funktion für den Timer
    }
  }, [alert]);  

  useEffect(() => {
    const fetchUserProfile = () => {
      const token = localStorage.getItem('authToken');
      axiosInstance
        .get('/profile/user-profile', {
          headers: {
            Authorization: `${token}`,
          },
        })
        .then((response) => {
          if (response.data.profile) {
            setUserProfile(response.data.profile);
            setEditedProfile({
              name: response.data.profile.name,
              email: response.data.profile.email,
              profileImageUrl: response.data.profile.profileImageUrl,
              schoolName: response.data.profile.schoolName || '',
              schoolVerificationDocument: response.data.profile.schoolVerificationDocument,
            });
            setEmailConfirmationPending(response.data.profile?.emailConfirmationPending || false);
          }
        })
        .catch((error) => {
          console.error('Fehler beim Abrufen des Benutzerprofils:', error);
        });
    };

    fetchUserProfile();

    // Reset `shouldReloadProfile` after fetching the profile
    if (shouldReloadProfile) {
      setShouldReloadProfile(false);
    }
  }, [shouldReloadProfile]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    const token = localStorage.getItem('authToken');

    // Überprüfe, ob der Schulname geändert wurde
    const schoolNameChanged = editedProfile.schoolName !== userProfile?.schoolName;
    const nameChanged = editedProfile.name !== userProfile?.name;
    const emailChanged = editedProfile.email !== userProfile?.email;
    const profileImageChanged = editedProfile.profileImageUrl !== userProfile?.profileImageUrl;

    // needed for schoolname change in case schoolVerified is not true
    let doNotCreateNewSchool = false;

    if (userProfile?.schoolVerified && schoolNameChanged) {
      alert('Die Schulbestätigung wurde erfolgreich geprüft. Eine Änderung des Schulnamens ist nicht erlaubt.');
      return; // Speichern verhindern
    } else if (!userProfile?.schoolVerified && userProfile?.schoolId && schoolNameChanged) {
      doNotCreateNewSchool = true;
    } else if ((userProfile?.schoolId && nameChanged) || (userProfile?.schoolId && emailChanged)) {
      doNotCreateNewSchool = true;
    }

    if (!editedProfile.schoolName.trim()) {
      alert('Bitte geben Sie einen gültigen Schulnamen ein.');
      return; // Speichern verhindern
    }

    let saveSchool;
    if (!editedProfile.schoolName && selectedSchool === null) {
      // Wenn kein Schulname vorhanden ist und keine Schule ausgewählt wurde, Schule entfernen
      saveSchool = axiosInstance.post(
        '/profile/remove-school', 
        {},
        { headers: { Authorization: `${token}` } }
      );
      setIsCreatingNewSchool(false);
    } else if (selectedSchool) {
      // Wenn eine Schule aus dem Vorschlag ausgewählt wurde, Schule zuordnen
      saveSchool = axiosInstance.post(
        '/profile/assign-school',
        { schoolName: selectedSchool.name }, // Verwende die Schul-ID für `assign-school`
        { headers: { Authorization: `${token}` } }
      );
      setIsCreatingNewSchool(false);
    } else if (isCreatingNewSchool && !doNotCreateNewSchool) {
      // Wenn eine neue Schule erstellt werden soll
      saveSchool = axiosInstance.post(
        '/profile/create-and-assign-school',
        { schoolName: editedProfile.schoolName }, // Schulname für die neue Schule
        { headers: { Authorization: `${token}` } }
      );
      setIsCreatingNewSchool(false);
    } else {
      // Keine Änderungen an der Schule
      saveSchool = Promise.resolve();
    }

    let saveMail = Promise.resolve();
    if (emailChanged) {
      saveMail = axiosInstance.post(
        '/profile/request-email-change',
        { newEmail: editedProfile.email },
        { headers: { Authorization: `${token}` } }
      ).then(() => {
        setAlert({
          severity: "info",
          message: "Bitte überprüfen Sie Ihre neue E-Mail-Adresse, um die Änderung zu bestätigen."
        });
        setEmailConfirmationPending(true);        
        return true;
      }).catch((error) => {
        if (error.response && error.response.status === 400) {
          setAlert({
            severity: "error",
            message: "Diese E-Mail-Adresse ist bereits registriert. Bitte wählen Sie eine andere."
          });
          return false;
        } else {
          setAlert({
            severity: "error",
            message: "Fehler beim Anfordern der E-Mail-Änderung. Bitte versuchen Sie es erneut."
          });
        }
        return Promise.reject(error);
      });      

      // Setze die alte E-Mail-Adresse wieder zurück, bis der Nutzer die neue bestätigt
      setEditedProfile((prevProfile) => ({
        ...prevProfile,
        email: userProfile?.email,
      }));
    }

    // Speichert den neuen Benutzernamen
    let saveUser = Promise.resolve(); // Standardmäßig kein API-Call
    if (nameChanged || schoolNameChanged || profileImageChanged) {
      saveUser = axiosInstance.post(
        '/profile/user-profile',
        { name: editedProfile.name, schoolName: editedProfile.schoolName, profileImageUrl: editedProfile.profileImageUrl},
        { headers: { Authorization: `${token}` } }
      );
    }
  
    saveMail.then((mailSuccess) => {
      // Falls E-Mail fehlgeschlagen oder 400 => Abbruch
      if (emailChanged && !mailSuccess) return;

      // E-Mail war ok => Speichere Schule + Userdaten
      return Promise.all([saveSchool, saveUser]);
    })
    .then((results) => {
      // results ist undefined, wenn oben abgebrochen wurde
      if (!results) return;
      setUserProfile({
        ...userProfile,
        name: editedProfile.name, // Aktualisiere den Namen im Frontend
        schoolName: selectedSchool?.name || editedProfile.schoolName, // Aktualisiere den Schulnamen im Frontend
        profileImageUrl: editedProfile.profileImageUrl
      });
      setAlert({
        severity: "success",
        message: "Die Änderungen wurden erfolgreich gespeichert!"
      });
      setIsEditing(false); // Bearbeitungsmodus beenden
      setShouldReloadProfile(true);
    })
    .catch((error) => {
      setAlert({
        severity: "error",
        message: "Beim Speichern ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder wenden Sie sich an info@schullogbuch24.de."
      });
      console.error('Fehler beim Speichern des Benutzerprofils:', error);
    });    
  };

  const handleResendConfirmationEmail = (email) => {
    const token = localStorage.getItem('authToken');
    
    axiosInstance.post('/profile/resend-confirmation-email', { email }, {
      headers: {
        Authorization: `${token}`,
      },
    })
    .then(() => {
      setAlert({
        severity: "success",
        message: "Eine neue Bestätigungs-Mail wurde an Ihre E-Mail-Adresse gesendet."
      });
    })
    .catch((error) => {
      setAlert({
        severity: "error",
        message: "Fehler beim Senden der Bestätigungs-Mail. Bitte versuchen Sie es später erneut."
      });
      console.error("Fehler beim Senden der Bestätigungs-Mail:", error);
    });
  };  
  
  const handleChange = (event, source, value = null) => {
    setAlert({
      severity: "info",
      message: "Sie bearbeiten gerade das Benutzerprofil. Vergessen Sie nicht zu speichern!"
    });
    if (source === "textField") {
      const name = event ? event.target.name : 'schoolName'; // Standardmäßig auf 'schoolName' setzen, falls e null ist
      const newValue = event ? event.target.value : value; // Falls value übergeben wurde (bei Autocomplete), verwende es

      setEditedProfile({
        ...editedProfile,
        [name]: newValue,
      });

      if (name === 'schoolName') {
        // Live-Suche für Schulen aus der Datenbank
        axiosInstance
          .get(`/profile/school-search?query=${newValue}`) // Triggert die Suche basierend auf dem eingegebenen Wert
          .then((response) => {
            // Filtere Schulen, die mit "Test" anfangen, aus den Vorschlägen heraus
            const filteredSchools = response.data.schools.filter(school => !school.name?.toLowerCase().startsWith('test'));
            setSchoolSearchResults(filteredSchools); // Speichert die gefilterten Suchergebnisse
          })
          .catch((error) => {
            console.error('Fehler bei der Schulsuche:', error);
          });
      }
    } else if (source === "profileUpload") {
      handleUserProfileLogoUpload(event);
    } else if (source === "schoolConfirmationUpload") {
      handleSchoolConfirmationUpload(event);
    }
  };

  return (
    <Box sx={{ padding: '2rem', maxWidth: '700px', display: 'flex', flexDirection: 'column', gap: '1rem', mt: '0.6rem' }}>
      <Box sx={{ border: '1px solid #ccc', borderRadius: '8px'}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50px',
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f9f9f9',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#333',
              letterSpacing: '0.5px'
            }}
          >
            Benutzerprofil
          </Typography>
        </Box>

        {alert && (
          <Box sx={{ maxWidth: '700px', paddingLeft: '2rem', paddingRight: '2rem' }}>
            <Alert severity={alert.severity}>
              {alert.message}
            </Alert>
          </Box>
        )}

        {userProfile || isEditing ? (
          <Box sx={{ position: 'relative', padding: '1rem', paddingLeft: '2rem', paddingRight: '2rem' }}>

          {!isEditing && (
            <Button
              variant="contained"
              size="small"
              color='primary'
              sx={{
                backgroundColor: 'primary.light',
                color: '#fff',
                position: 'absolute',
                top: '1rem',
                right: '2rem',
                border: 'none',
                '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
              }}
              onClick={handleEditClick}
            >
              Bearbeiten
            </Button>
          )}

            {!isEditing ? (
              <Box>
                {[
                  { label: 'Profilbild', value: <Avatar alt="Profilbild" src={editedProfile.profileImageUrl || userProfile?.logoUrl} sx={{ width: 100, height: 100 }} /> },
                  { label: 'Name des Benutzers', value: userProfile?.name },
                  { label: 'E-Mail-Adresse', value: userProfile?.email },
                  { label: 'Name der Schule', value: userProfile?.schoolName || '' },
                  { label: 'Schulbestätigung', value: userProfile?.schoolVerificationDocument ? (
                    userProfile?.schoolVerificationDocument.endsWith('.pdf') ? (
                        <iframe src={userProfile?.schoolVerificationDocument} width="100%" height="400px" title={confirmationFile}></iframe>
                      ) : (
                        <img src={userProfile?.schoolVerificationDocument} alt="Schulbestätigung Vorschau" width="100%" />
                      )
                    ) : 'Keine Datei hochgeladen'
                  },
                ].map((field, index) => (
                  <Box key={index} sx={{ marginBottom: '1rem' }}>
                    <Typography color="black" variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {field.label}
                    </Typography>
                    <Typography sx={{ fontStyle: field.value ? 'normal' : 'italic', color: field.value ? 'inherit' : 'gray' }}>
                      {field.value || 'Nicht ausgefüllt'}
                    </Typography>
                    {emailConfirmationPending && field.label === 'E-Mail-Adresse' && (
                      <Box sx={{ mt: 1 }}>
                        <Alert severity="warning">
                          Ihre E-Mail-Adresse wurde geändert. Bitte bestätigen Sie Ihre neue Adresse innerhalb von 24 Stunden.
                          <br />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Bestätigungslink nicht erhalten oder abgelaufen?{" "}
                            <Link
                              href="#"
                              onClick={(e) => {
                                e.preventDefault(); // Verhindert, dass die Seite neu geladen wird
                                handleResendConfirmationEmail(editedProfile.email);
                              }}
                              style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
                            >
                              Fordern Sie eine neue Bestätigungs-Mail an.
                            </Link>
                          </Typography>
                        </Alert>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box component="form">
                {/* Profilbild ändern */}
                <Typography variant="h6">Profilbild</Typography>
                <Avatar alt="Profilbild" src={editedProfile.profileImageUrl || userProfile?.logoUrl} sx={{ width: 100, height: 100, mb: '1rem' }} />
                {editedProfile.profileImageUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => {
                      setEditedProfile((prevProfile) => ({ ...prevProfile, profileImageUrl: '' }));
                      setAlert({
                        severity: "info",
                        message: "Sie bearbeiten gerade das Schulprofil. Vergessen Sie nicht zu speichern!"
                      });
                    }}
                    sx={{ mr: '1rem', color: 'error.light', '&:hover': { color: 'error.dark', backgroundColor: '#fff' } }}
                  >
                    Profilbild entfernen
                  </Button>
                )}
                <input
                  type="file"
                  onChange={(e) => handleChange(e, "profileUpload")}
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button
                    size="small"
                    color="primary"
                    sx={{
                      color: 'primary.light',
                      '&:hover': {
                        color: 'primary.dark'
                      }
                    }}
                    startIcon={<FileUploadIcon />}
                    variant="outlined"
                    component="span"
                    type="button"
                  >
                    Hochladen
                  </Button>
                </label>
                {isUploading && (
                  <Box sx={{ width: '100%', mt: 2}}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}

                {/* Name des Benutzers */}
                <TextField
                  label="Name des Benutzers"
                  name="name"
                  value={editedProfile.name}
                  onChange={(e) => handleChange(e, "textField")}
                  fullWidth
                  margin="normal"
                  sx={{ mt: '2rem' }}
                />
                <TextField
                  label="E-Mail-Adresse"
                  name="email"
                  value={editedProfile.email}
                  onChange={(e) => handleChange(e, "textField")}
                  fullWidth
                  margin="normal"
                />

                {/* Schulname mit Live-Suche */}
                <Autocomplete
                  freeSolo
                  options={schoolSearchResults}
                  value={
                    schoolSearchResults.find((school) => school.name === editedProfile.schoolName) || { name: editedProfile.schoolName }
                  }
                  inputValue={inputValue}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    return option.name || '';
                  }}
                  onChange={(event, newValue) => {
                    setAlert({
                      severity: "info",
                      message: "Sie bearbeiten gerade das Schulprofil. Vergessen Sie nicht zu speichern!"
                    });
                    if (!newValue) {
                      // Wenn kein Wert ausgewählt ist
                      setEditedProfile({ ...editedProfile, schoolName: '' });
                      setSelectedSchool(null);
                      setIsCreatingNewSchool(false);
                    } else if (newValue?.inputValue) {
                      // Wenn eine neue Schule eingegeben wird
                      setEditedProfile({ ...editedProfile, schoolName: newValue.inputValue });
                      setIsCreatingNewSchool(true);
                      setSelectedSchool(null);
                    } else if (newValue?.name) {
                      // Wenn eine bestehende Schule ausgewählt wird
                      setEditedProfile({ ...editedProfile, schoolName: newValue.name });
                      setSelectedSchool(newValue); // Setze die ausgewählte Schule
                      setIsCreatingNewSchool(false);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue); // Halte den aktuellen Eingabewert
                  
                    if (newInputValue.trim() === '') {
                      setEditedProfile({ ...editedProfile, schoolName: '' });
                      setSelectedSchool(null);
                      setIsCreatingNewSchool(false);
                      setSchoolSearchResults([]); // Leere Suchergebnisse, wenn das Feld leer ist
                    } else {
                      setEditedProfile({ ...editedProfile, schoolName: newInputValue });
                  
                      // Trigger Suche nach existierenden Schulen
                      axiosInstance
                        .get(`/profile/school-search?query=${newInputValue}`)
                        .then((response) => {
                          // Filtere Schulen, die mit "Test" anfangen, aus den Vorschlägen heraus
                          const filteredSchools = response.data.schools.filter(school => !school.name?.toLowerCase().startsWith('test'));
                          setSchoolSearchResults(filteredSchools); // Aktualisiere die gefilterten Suchergebnisse
                        })
                        .catch((error) => {
                          console.error('Fehler bei der Schulsuche:', error);
                        });
                    }
                  }}                
                  onBlur={() => {
                    if (!inputValue) {
                      // Wenn das Feld leer ist
                      setEditedProfile({ ...editedProfile, schoolName: '' });
                      setSelectedSchool(null);
                      setIsCreatingNewSchool(false);
                    } else if (
                      !schoolSearchResults.some((school) => school.name === inputValue) &&
                      !inputValue.startsWith('Neue Schule hinzufügen:')
                    ) {
                      // Falls der Wert keine existierende Schule ist
                      setEditedProfile({ ...editedProfile, schoolName: inputValue });
                      setIsCreatingNewSchool(true);
                      setSelectedSchool(null);
                    } else {
                      // Wenn der Wert existiert
                      const existingSchool = schoolSearchResults.find((school) => school.name === inputValue);
                      if (existingSchool) {
                        setEditedProfile({ ...editedProfile, schoolName: existingSchool.name });
                        setSelectedSchool(existingSchool);
                        setIsCreatingNewSchool(false);
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Name der Schule"
                      margin="normal"
                      fullWidth
                      required
                      disabled={userProfile?.schoolVerified} // Deaktiviert das Feld
                      helperText={
                        userProfile?.schoolVerified
                          ? 'Die Schulbestätigung wurde erfolgreich geprüft. Eine Änderung ist nicht möglich.'
                          : ''
                      }
                    />
                  )}
                  filterOptions={(options, params) => {
                    const filtered = createFilterOptions()(options, params);

                    // Option hinzufügen, um eine neue Schule zu erstellen
                    if (params.inputValue !== '') {
                      filtered.push({
                        inputValue: params.inputValue,
                        name: `Neue Schule hinzufügen: "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  renderOption={(props, option, { inputValue }) => {
                    const matches = option.name.toLowerCase().includes(inputValue.toLowerCase());
                    return (
                      <li {...props}>
                        <span style={{ fontWeight: matches ? 700 : 400 }}>
                          {option.name}
                        </span>
                      </li>
                    );
                  }}
                  disabled={userProfile?.schoolVerified}
                />

                {/* Schulbestätigung hochladen */}
                <Typography variant="h6">Schulbestätigung</Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Bitte laden Sie einen Nachweis hoch, der bestätigt, dass Sie Angestellter dieser Schule sind. Sobald die Prüfung Ihres Nachweises erfolgreich abgeschlossen ist, können Sie auf das Schulprofil zugreifen.
                </Typography>
                <input
                  type="file"
                  onChange={(e) => handleChange(e, "schoolConfirmationUpload")}
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="confirmation-upload"
                />
                <label htmlFor="confirmation-upload">
                  <Button color="primary" size="small" startIcon={<FileUploadIcon />} variant="outlined" component="span" type="button">
                    Bestätigung hochladen
                  </Button>
                </label>
                {isUploading && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}
                {confirmationPreview && (
                  confirmationFile.name.endsWith('.pdf') ? (
                    <iframe src={confirmationPreview} width="100%" height="400px" title={confirmationFile}></iframe>
                  ) : (
                    <img src={confirmationPreview} alt={confirmationFile.name} width="100%" />
                  )
                )}

                <Box>
                <Button
                  startIcon={<SaveIcon />}
                  color="success"
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    mt: 2,
                    bgcolor: 'primary.light',
                    '&:hover': {
                      bgcolor: 'primary.dark', // Hover-Farbe setzen
                      color: 'primary.contrastText'
                    },
                  }}
                  type="button"
                >
                  Speichern
                </Button>

                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ border: '1px dashed grey', padding: '1rem', borderRadius: '8px' }}>
            <Typography variant="h6">Lade Profil...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserProfilePage;
